import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, ShieldAlert, Activity } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function SalesContent() {
  return <div className="bg-cream-100 relative" data-id="element-50">
      {/* Divider */}
      <div className="h-px w-full bg-cream-300 absolute top-0" data-id="element-51" />

      {/* Opening Hook */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto text-center" data-id="element-52">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} data-id="element-53">
          <h2 className="text-3xl md:text-4xl font-bold text-ocean mb-8" data-id="element-54">
            Hay un momento que todo padre reconoce.
          </h2>

          <div className="space-y-6 text-lg text-ink-700 font-light leading-relaxed text-left" data-id="element-55">
            <p data-id="element-56">
              Tu hijo comete un error en el partido. El balón sale fuera. Y
              antes de que el juego continúe, te busca con la mirada.
            </p>
            <p className="text-xl text-ink-900 font-medium border-l-2 border-slate-teal pl-4 py-1" data-id="element-57">
              Lo que ve en tu cara en ese segundo importa más que cualquier
              entrenamiento.
            </p>
            <p data-id="element-58">No se está jugando un pase. Se está jugando su identidad.</p>
            <p data-id="element-59">
              Ese partido invisible — construido a lo largo de cientos de
              reacciones, palabras y momentos de presión acumulada — es el que
              realmente decide su trayectoria. No la técnica. No la velocidad.{' '}
              <span className="text-slate-teal font-semibold" data-id="element-60">La mente.</span>
            </p>
          </div>
        </motion.div>
      </section>

      {/* The Problem */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-cream-200/50 relative" data-id="element-61">
        <div className="max-w-4xl mx-auto" data-id="element-62">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-center mb-12" data-id="element-63">
            <ShieldAlert className="w-12 h-12 text-slate-teal mx-auto mb-6" data-id="element-64" />
            <h2 className="text-3xl md:text-4xl font-bold text-ocean mb-6" data-id="element-65">
              El problema real del que nadie habla
            </h2>
            <p className="text-lg text-ink-700 max-w-2xl mx-auto" data-id="element-66">
              La mayoría de los recursos sobre deporte juvenil hablan de
              táctica, físico, técnica. Muy pocos hablan de lo que realmente
              decide si un niño crece o se bloquea:{' '}
              <strong className="text-ink-900 font-medium" data-id="element-67">
                Lo que ocurre dentro de él.
              </strong>
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-8" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-68">
            <motion.div variants={fadeIn} className="bg-white p-8 rounded-xl border border-cream-300" data-id="element-69">
              <p className="text-ink-700 leading-relaxed" data-id="element-70">
                Cuando un niño cree que su valor depende de hacerlo bien, el
                error se convierte en amenaza. Su cuerpo se tensa. Se encoge.{' '}
                <span className="text-ink-900 font-medium" data-id="element-71">
                  Juega para no perder en lugar de jugar para ganar.
                </span>
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="bg-white p-8 rounded-xl border border-cream-300" data-id="element-72">
              <p className="text-ink-700 leading-relaxed" data-id="element-73">
                No porque sea débil. Porque su cerebro aprendió que equivocarse
                es peligroso. Y ese aprendizaje no ocurrió en el campo.{' '}
                <span className="text-ink-900 font-medium" data-id="element-74">
                  Ocurrió en casa.
                </span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why what you tried didn't work */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto" data-id="element-75">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} data-id="element-76">
          <h2 className="text-3xl md:text-4xl font-bold text-ocean mb-10 text-center" data-id="element-77">
            Por qué lo que has intentado no funciona
          </h2>

          <ul className="space-y-8" data-id="element-78">
            <li className="flex items-start" data-id="element-79">
              <div className="flex-shrink-0 mt-1.5" data-id="element-80">
                <div className="w-2 h-2 rounded-full bg-slate-teal" data-id="element-81" />
              </div>
              <p className="ml-4 text-lg text-ink-700" data-id="element-82">
                <strong className="text-ink-900 block mb-1 font-medium" data-id="element-83">
                  Más entrenamientos técnicos no eliminan el miedo al juicio.
                </strong>
                El techo real no está en sus piernas — está en lo que cree sobre
                sí mismo.
              </p>
            </li>
            <li className="flex items-start" data-id="element-84">
              <div className="flex-shrink-0 mt-1.5" data-id="element-85">
                <div className="w-2 h-2 rounded-full bg-slate-teal" data-id="element-86" />
              </div>
              <p className="ml-4 text-lg text-ink-700" data-id="element-87">
                <strong className="text-ink-900 block mb-1 font-medium" data-id="element-88">
                  El análisis en el coche de vuelta
                </strong>
                Aunque sea bienintencionado, se recibe como juicio cuando el
                sistema nervioso todavía está dentro del partido.
              </p>
            </li>
            <li className="flex items-start" data-id="element-89">
              <div className="flex-shrink-0 mt-1.5" data-id="element-90">
                <div className="w-2 h-2 rounded-full bg-slate-teal" data-id="element-91" />
              </div>
              <p className="ml-4 text-lg text-ink-700" data-id="element-92">
                <strong className="text-ink-900 block mb-1 font-medium" data-id="element-93">
                  Decirle "relájate" no funciona.
                </strong>
                Su cuerpo está en modo supervivencia. Necesita aprender a usar
                esa energía, no eliminarla.
              </p>
            </li>
          </ul>
        </motion.div>
      </section>

      {/* The Unique Mechanism */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-cream-200/30 relative overflow-hidden" data-id="element-94">
        <div className="max-w-5xl mx-auto relative z-10" data-id="element-95">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-96">
            <h2 className="text-3xl md:text-4xl font-bold text-ocean mb-6" data-id="element-97">
              El Juego Invisible no es un libro sobre fútbol.
            </h2>
            <p className="text-xl text-slate-teal font-medium" data-id="element-98">
              Es un protocolo de transformación mental basado en investigación
              real:
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-99">
            <motion.div variants={fadeIn} className="bg-white p-6 rounded-xl border border-cream-300" data-id="element-100">
              <Brain className="w-8 h-8 text-slate-teal mb-4" data-id="element-101" />
              <h3 className="text-ocean font-bold mb-2" data-id="element-102">Neuroplasticidad</h3>
              <p className="text-sm text-ink-500" data-id="element-103">
                El cerebro de tu hijo se moldea ahora más rápido que en
                cualquier otra etapa. Lo que repite hoy se instala mañana.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white p-6 rounded-xl border border-cream-300" data-id="element-104">
              <Activity className="w-8 h-8 text-slate-teal mb-4" data-id="element-105" />
              <h3 className="text-ocean font-bold mb-2" data-id="element-106">
                El principio de Hebb
              </h3>
              <p className="text-sm text-ink-500" data-id="element-107">
                Los patrones de miedo se formaron por repetición. Y por
                repetición consciente pueden transformarse.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white p-6 rounded-xl border border-cream-300" data-id="element-108">
              <Target className="w-8 h-8 text-slate-teal mb-4" data-id="element-109" />
              <h3 className="text-ocean font-bold mb-2" data-id="element-110">
                El experimento de Harvard
              </h3>
              <p className="text-sm text-ink-500" data-id="element-111">
                El cerebro no distingue entre práctica física y práctica mental
                vívida. Lo que tu hijo repite en su mente también entrena.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white p-6 rounded-xl border border-cream-300 lg:col-start-1 lg:col-end-3 lg:ml-auto lg:mr-3 lg:w-full max-w-md" data-id="element-112">
              <h3 className="text-ocean font-bold mb-2" data-id="element-113">El Efecto Pigmalión</h3>
              <p className="text-sm text-ink-500" data-id="element-114">
                Lo que tú crees sobre tu hijo determina, en parte, quién se
                convierte.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white p-6 rounded-xl border border-cream-300 lg:col-start-3 lg:col-end-4 lg:mr-auto lg:ml-3 lg:w-full max-w-md" data-id="element-115">
              <h3 className="text-ocean font-bold mb-2" data-id="element-116">
                La psicología del error
              </h3>
              <p className="text-sm text-ink-500" data-id="element-117">
                Cómo tu hijo interpreta el fallo decide si crece o se bloquea
                (Seligman).
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>;
}